import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Section } from "../layout/Section";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { useCareerTimeline } from "../../features/career/hooks/useCareer";
import { Briefcase, GraduationCap, Rocket, ChevronRight } from "lucide-react";
gsap.registerPlugin(ScrollTrigger);
const TYPE_MAP = {
    JOB: "work",
    FREELANCE: "freelance",
    EDUCATION: "education",
    VOLUNTEER: "volunteer",
    ACHIEVEMENT: "work",
    PLAN: "plan",
    BLOCKCHAIN: "blockchain",
    FOUNDER: "founder",
};
const TYPE_CONFIG = {
    work: { icon: Briefcase, label: "Work" },
    education: { icon: GraduationCap, label: "Education" },
    founder: { icon: Rocket, label: "Founder" },
    freelance: { icon: Briefcase, label: "Freelance" },
    volunteer: { icon: GraduationCap, label: "Volunteer" },
    plan: { icon: Rocket, label: "Plan" },
    blockchain: { icon: Rocket, label: "Blockchain" },
};
/* ── Single timeline card ─────────────────────────────────────────── */
function TimelineCard({ entry, index }) {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-80px" });
    const isEven = index % 2 === 0;
    const Icon = TYPE_CONFIG[entry.type]?.icon ?? Briefcase;
    return (_jsxs("div", { ref: ref, className: `relative flex items-start md:gap-0 ${isEven ? "md:flex-row" : "md:flex-row-reverse"}`, children: [_jsx(motion.div, { initial: { opacity: 0, x: isEven ? -40 : 40 }, animate: isInView ? { opacity: 1, x: 0 } : {}, transition: { duration: 0.5, delay: 0.15, ease: "easeOut" }, className: "flex-1 md:w-[calc(50%-2rem)]", children: _jsxs("div", { className: "group relative border border-border rounded-lg p-5 sm:p-6 bg-surface/50 backdrop-blur-sm hover:border-text-muted/30 transition-colors duration-300", children: [_jsx("span", { className: "absolute top-4 right-4 text-[10px] font-mono tracking-widest text-text-muted uppercase", children: entry.date }), _jsxs("div", { className: "flex items-center gap-2 mb-3", children: [_jsx("span", { className: "flex items-center justify-center w-7 h-7 rounded-full border border-border bg-background", children: _jsx(Icon, { className: "w-3.5 h-3.5 text-text-muted" }) }), _jsx(Badge, { variant: "outline", className: "text-[10px] uppercase tracking-wider", children: TYPE_CONFIG[entry.type]?.label ?? entry.type })] }), _jsx("h3", { className: "text-base sm:text-lg font-semibold text-text-primary leading-tight pr-20", children: entry.title }), _jsxs("p", { className: "mt-1 text-sm font-medium text-text-muted flex items-center gap-1", children: [_jsx(ChevronRight, { className: "w-3 h-3 opacity-50" }), entry.organisation] }), _jsx("p", { className: "mt-3 text-sm text-text-muted/80 leading-relaxed", children: entry.description }), _jsx("div", { className: "absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-border to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" })] }) }), _jsx("div", { className: "hidden md:flex flex-col items-center w-16 shrink-0", children: _jsx(motion.div, { initial: { scale: 0 }, animate: isInView ? { scale: 1 } : {}, transition: {
                        duration: 0.3,
                        delay: 0.1,
                        type: "spring",
                        stiffness: 300,
                    }, className: "relative w-4 h-4 rounded-full border-2 border-accent bg-background z-10", children: _jsx("span", { className: "absolute inset-[3px] rounded-full bg-accent" }) }) }), _jsx("div", { className: "hidden md:block flex-1 md:w-[calc(50%-2rem)]" }), _jsx(motion.div, { initial: { scale: 0 }, animate: isInView ? { scale: 1 } : {}, transition: { duration: 0.3, type: "spring", stiffness: 300 }, className: "absolute -left-2 top-6 w-3 h-3 rounded-full border-2 border-accent bg-background md:hidden z-10", children: _jsx("span", { className: "absolute inset-[2px] rounded-full bg-accent" }) })] }));
}
/* ── Career section ───────────────────────────────────────────────── */
function formatPeriod(start, end, current) {
    const fmt = (d) => {
        const date = new Date(d);
        return date.toLocaleDateString("en-US", {
            month: "short",
            year: "numeric",
        });
    };
    return current || !end
        ? `${fmt(start)} — Present`
        : `${fmt(start)} — ${fmt(end)}`;
}
export const CareerSection = () => {
    const [filter, setFilter] = useState("all");
    const progressLineRef = useRef(null);
    const timelineRef = useRef(null);
    const { data: raw } = useCareerTimeline();
    const experiences = (raw ?? []).map((e) => ({
        id: e.id,
        type: TYPE_MAP[e.type] ?? "work",
        title: e.title ?? "",
        organisation: e.organisation ?? "",
        description: e.description ?? "",
        date: e.date ?? formatPeriod(e.startDate, e.endDate, e.current),
        startDate: e.startDate ?? "",
        endDate: e.endDate ?? null,
        current: e.current ?? false,
        points: e.points ?? [],
        keySkills: e.keySkills ?? [],
        order: e.order ?? 0,
    }));
    const filtered = experiences.filter((entry) => filter === "all" || entry.type === filter);
    // Scroll-driven progress line
    useEffect(() => {
        if (!progressLineRef.current || !timelineRef.current)
            return;
        const ctx = gsap.context(() => {
            gsap.fromTo(progressLineRef.current, { scaleY: 0 }, {
                scaleY: 1,
                ease: "none",
                scrollTrigger: {
                    trigger: timelineRef.current,
                    start: "top 80%",
                    end: "bottom 20%",
                    scrub: 0.3,
                },
            });
        });
        return () => ctx.revert();
    }, [filtered]);
    if (experiences.length === 0)
        return null;
    return (_jsx(Section, { id: "career", children: _jsxs("div", { className: "space-y-12", children: [_jsxs("div", { className: "flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4", children: [_jsxs("div", { children: [_jsx("p", { className: "text-label text-text-muted uppercase tracking-widest mb-2", children: "Experience" }), _jsx("h2", { className: "text-display-lg font-serif text-text-primary", children: "Career Timeline" })] }), _jsx("div", { className: "flex gap-2", children: ["all", "work", "founder", "blockchain", "education"].map((type) => (_jsx(Button, { variant: filter === type ? "primary" : "ghost", size: "sm", onClick: () => setFilter(type), className: "capitalize text-xs", children: type }, type))) })] }), _jsxs("div", { ref: timelineRef, className: "relative pl-6 md:pl-0", children: [_jsx("div", { className: "absolute left-1 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-0.5 bg-border/40" }), _jsx("div", { ref: progressLineRef, className: "absolute left-1 md:left-1/2 md:-translate-x-px top-0 bottom-0 w-0.5 bg-accent origin-top", style: { transformOrigin: "top center" } }), _jsx("div", { className: "space-y-8 md:space-y-12", children: filtered.map((entry, i) => (_jsx(TimelineCard, { entry: entry, index: i }, entry.id))) })] })] }) }));
};
//# sourceMappingURL=CareerSection.js.map